import { ReactNode, useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { Card, Link, Text, Title } from 'components/common'
import {
  BookingDetail,
  BookingDetailsEditModal,
  BookingDetailsEditModalProps,
  BookingsUserMetaData,
  BookingUsageScheduleDetail,
  BookingUsageScheduleEditModal,
} from 'components/bookings'
import { BaseCardProps } from 'components/bookings/cards/types'
import { OrderPurpose } from 'lib/graphql/storeadmin'
import { staticCustomerType } from 'lib/constants'
import format from 'lib/str-formatter'
import RemainingDiscountCard from './RemainingDiscountCard'

interface ItemProps {
  title: string
  editable?: boolean
  children?: ReactNode
  onEdit?: () => void
}

const Item = ({
  title,
  children,
  editable,
  onEdit,
}: ItemProps): JSX.Element => (
  <>
    <div className={clsx('flex', 'justify-between', 'items-center', 'mb-3')}>
      <Title level={5} className={clsx('mb-0')}>
        {title}
      </Title>
      {editable && <Link onClick={onEdit}>編集</Link>}
    </div>
    <div>{children}</div>
  </>
)

interface linerTextDesignProps {
  title: string
  content: string | number | ReactNode
}

const LinerTextDesign = ({
  title,
  content,
}: linerTextDesignProps): JSX.Element => {
  return (
    <>
      <div className="my-2">
        <Text type="secondary">{title}</Text>
        <br />
        <Text strong>{content}</Text>
      </div>
    </>
  )
}

export interface BookingInfoCardProps
  extends BaseCardProps,
    Pick<BookingDetailsEditModalProps, 'bookingDetail'> {
  customer: {
    id?: string
    firstName?: string
    lastName?: string
    customerType: string
    corporationName?: string
    email: string
    phone: string
  }
  billingInfo?: {
    phone: string
    zip: string
    prefecture: string
    city: string
    address1: string
    address2: string
  }
  discountDetail?: {
    discount: number
    limit: number
  }
  usageScheduleDetails?: {
    approval: boolean
    organizer: string
    eventName: string
    publishableDate: moment.Moment
  }
  loading?: boolean
  disabled?: boolean
  editable: boolean
  onDetailHold?: (values: BookingDetail) => void
  onUsageScheduleDetailsHold?: (values: BookingUsageScheduleDetail) => void
  onUserMetaDataHold?: (values: BookingsUserMetaData) => void
  isArena?: boolean
}

export const BookingInfoCard = ({
  customer,
  billingInfo,
  bookingDetail,
  discountDetail,
  usageScheduleDetails,
  loading,
  disabled,
  editable,
  // onBillingInfoClick,
  isArena,
  onDetailHold,
  onUsageScheduleDetailsHold,
  onUserMetaDataHold,
  ...rest
}: BookingInfoCardProps): JSX.Element => {
  const [detailVisible, setDetailVisible] = useState<boolean>(false)
  const [showUsageSchedule, setUsageSchedule] = useState(false)

  const viewPurpose = (purpose: string) => {
    switch (purpose) {
      case OrderPurpose.General:
        return <Text strong>一般利用</Text>
      case OrderPurpose.Event:
        return <Text strong>イベント利用</Text>
      default:
        return <Text strong>その他</Text>
    }
  }

  return (
    <>
      <Card
        shadow
        loading={loading}
        {...rest}
        bodyStyle={{ width: '100%', overflowX: 'auto' }}
        style={{ marginBottom: '10px' }}
      >
        {customer?.id && (
          <>
            <Title level={5}>お客様情報</Title>
            <Link
              href={`/admin/customers/${customer.customerType?.toLowerCase()}/${
                customer.id
              }`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {customer.lastName || ''} {customer.firstName || ''}
            </Link>

            <Card.Divider />

            <Item title="連絡先情報" editable={false}>
              {customer?.customerType === staticCustomerType.corporate && (
                <LinerTextDesign
                  title="法人・団体名"
                  content={customer?.corporationName}
                />
              )}
              <LinerTextDesign
                title="メールアドレス"
                content={
                  customer?.email ? (
                    <Link href={`mailto:${customer.email}`}>
                      {customer.email}
                    </Link>
                  ) : (
                    '登録されていません。'
                  )
                }
              />
              <LinerTextDesign
                title="電話番号"
                content={
                  customer?.phone ? (
                    <Link href={format.phoneURI(customer.phone)}>
                      {format.phone(customer.phone)}
                    </Link>
                  ) : (
                    '登録されていません。'
                  )
                }
              />
            </Item>

            <Card.Divider />
          </>
        )}

        <Item
          title="予約詳細情報"
          onEdit={() => setDetailVisible(true)}
          editable={editable}
        >
          {!isArena && (
            <LinerTextDesign
              title="利用目的"
              content={
                bookingDetail?.purpose
                  ? viewPurpose(bookingDetail?.purpose)
                  : '未選択'
              }
            />
          )}
          <LinerTextDesign
            title="利用予定人数"
            content={
              bookingDetail?.numberOfPeople > 0
                ? `${bookingDetail?.numberOfPeople} 人`
                : '未設定'
            }
          />
          {bookingDetail?.purpose === OrderPurpose.Event && (
            <>
              <LinerTextDesign
                title="責任者氏名"
                content={
                  bookingDetail?.contactOfRepresentative?.name || '入力なし'
                }
              />
              <LinerTextDesign
                title="責任者連絡先メールアドレス"
                content={
                  bookingDetail?.contactOfRepresentative?.email || '入力なし'
                }
              />
              <LinerTextDesign
                title="責任者連絡先電話番号"
                content={
                  bookingDetail?.contactOfRepresentative?.phone || '入力なし'
                }
              />
              <LinerTextDesign
                title="物品販売"
                content={bookingDetail?.saleOfGoods ? 'あり' : 'なし'}
              />
              <LinerTextDesign
                title="入場料徴収"
                content={bookingDetail?.entryFee ? 'あり' : 'なし'}
              />
            </>
          )}
        </Item>

        <Card.Divider />
        <Item
          title="利用予定表"
          onEdit={() => setUsageSchedule(true)}
          editable={editable}
        >
          <LinerTextDesign
            title="利用予定表への掲載"
            content={
              usageScheduleDetails?.approval === true
                ? '許可する'
                : '許可しない'
            }
          />
          {usageScheduleDetails?.approval && (
            <>
              <LinerTextDesign
                title="掲載名称"
                content={usageScheduleDetails?.eventName}
              />
              <LinerTextDesign
                title="主催者"
                content={usageScheduleDetails?.organizer}
              />
              <LinerTextDesign
                title="掲載可能日"
                content={usageScheduleDetails?.publishableDate.toString()}
              />
            </>
          )}
        </Item>
        {customer?.customerType === 'CORPORATE' && (
          <>
            <Card.Divider />
            <RemainingDiscountCard id={customer?.id} />
          </>
        )}

        {/* TODO: 請求先情報実装後に追加 */}
        {/* <Item title='請求先情報' onEdit={onBillingInfoClick}>
        {billingInfo ? (
          <>
            <P>〒{billingInfo.zip}</P>
            <P>
              {billingInfo.prefecture} {billingInfo.city} {billingInfo.address1}{' '}
              {billingInfo.address2}
            </P>
            <P>{billingInfo.phone}</P>
          </>
        ) : (
          <Text mute>請求先情報は登録されていません。</Text>
        )}
      </Item>

      <Card.Divider /> */}
      </Card>

      <BookingDetailsEditModal
        visible={detailVisible}
        bookingDetail={bookingDetail}
        onOk={onDetailHold}
        onClose={setDetailVisible}
        isArena={isArena}
      />
      <BookingUsageScheduleEditModal
        visible={showUsageSchedule}
        usageScheduleDetails={usageScheduleDetails}
        onOk={onUsageScheduleDetailsHold}
        onClose={setUsageSchedule}
      />
      {/* <BookingsUserMetaDataEditModal
        showEligibleForExemption={setEligibleForExemption}
        visible={showUserMetaData}
        userMetaData={{
          ...discountDetail,
          showDiscount: showEligibleForExemption,
        }}
        onOk={onUserMetaDataHold}
        onClose={setUserMetaData}
      /> */}
    </>
  )
}
